import React, { useEffect, useState } from "react";
import {
  adminFetchBtwLabels,
  adminCreateBtwLabel,
  adminUpdateBtwLabel,
  adminArchiveBtwLabel,
} from "../../core/axios/endpoints";
import ConfirmModal from "../../components/confirm-modal";
import BtwLabelPill, { LABEL_COLOR_OPTIONS, getLabelColors } from "../../components/btw-label-pill";

const emptyForm = { name: "", color: "slate" };

const BtwLabelsPage = () => {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminFetchBtwLabels({ includeArchived: showArchived });
      setLabels(res?.data ?? res ?? []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [showArchived]);

  const startNew = () => {
    setEditing("new");
    setForm(emptyForm);
    setError(null);
  };

  const startEdit = (label) => {
    setEditing(label._id);
    setForm({
      name: label.name,
      color: label.color,
    });
    setError(null);
  };

  const cancel = () => {
    setEditing(null);
    setForm(emptyForm);
    setError(null);
  };

  const save = async () => {
    if (!form.name.trim()) {
      setError("Naam is verplicht.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editing === "new") {
        await adminCreateBtwLabel(form);
      } else {
        await adminUpdateBtwLabel(editing, form);
      }
      await load();
      cancel();
    } catch (e) {
      setError(e?.response?.data?.error ?? e?.message ?? "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const doArchive = async () => {
    if (!archiveTarget) return;
    setSaving(true);
    try {
      await adminArchiveBtwLabel(archiveTarget._id, !archiveTarget.archived);
      setArchiveTarget(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.error ?? "Mislukt.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-wrapper px-6 pt-24 pb-12">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-gray-900">
            Labels
          </h2>
          <p className="mt-1 text-xs text-gray-500">
            Tags die je orthogonaal aan workflow-status op een aanvraag kunt
            zetten. Eén aanvraag kan meerdere labels tegelijk hebben.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            Toon gearchiveerd
          </label>
          <button
            type="button"
            onClick={startNew}
            disabled={editing !== null}
            className="rounded-md bg-gray-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
          >
            + Nieuw label
          </button>
        </div>
      </div>

      {/* Editor */}
      {editing !== null ? (
        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50/30 p-5 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-gray-900">
            {editing === "new" ? "Nieuw label" : "Label bewerken"}
          </h3>
          <div className="space-y-4">
            <label className="block text-sm">
              <span className="font-medium text-gray-700">Naam</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Bijv. Wacht op klant"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                disabled={saving}
              />
            </label>

            <div>
              <span className="block text-sm font-medium text-gray-700">Kleur</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {LABEL_COLOR_OPTIONS.map((c) => {
                  const colors = getLabelColors(c);
                  const isSelected = form.color === c;
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm({ ...form, color: c })}
                      disabled={saving}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${colors.bg} ${colors.text} ${
                        isSelected ? "ring-2 ring-gray-900" : "opacity-70 hover:opacity-100"
                      }`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${colors.dot}`} />
                      {c}
                    </button>
                  );
                })}
              </div>
              <div className="mt-3">
                <span className="text-xs text-gray-500">Preview:</span>{" "}
                <BtwLabelPill label={{ name: form.name || "Label", color: form.color }} />
              </div>
            </div>

            {error ? (
              <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
                {error}
              </div>
            ) : null}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={cancel}
                disabled={saving}
                className="rounded-md border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="rounded-md bg-emerald-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {saving ? "Opslaan…" : "Opslaan"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Lijst */}
      <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200">
        {loading ? (
          <div className="p-10 text-center text-gray-500">Laden…</div>
        ) : labels.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            Geen labels. Maak er een aan.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {labels.map((label) => (
              <li key={label._id} className="flex items-center gap-4 p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <BtwLabelPill label={label} />
                    {label.archived ? (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                        Gearchiveerd
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => startEdit(label)}
                    disabled={editing !== null}
                    className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Bewerken
                  </button>
                  <button
                    type="button"
                    onClick={() => setArchiveTarget(label)}
                    disabled={editing !== null}
                    className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {label.archived ? "Terughalen" : "Archiveren"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmModal
        isOpen={!!archiveTarget}
        title={archiveTarget?.archived ? "Terughalen?" : "Archiveren?"}
        message={
          archiveTarget?.archived
            ? `Het label "${archiveTarget?.name}" wordt weer beschikbaar.`
            : `Het label "${archiveTarget?.name}" wordt gearchiveerd en losgekoppeld van alle aanvragen waar het op staat.`
        }
        confirmLabel={archiveTarget?.archived ? "Terughalen" : "Archiveren"}
        cancelLabel="Annuleren"
        busy={saving}
        onConfirm={doArchive}
        onCancel={() => setArchiveTarget(null)}
      />
    </div>
  );
};

export default BtwLabelsPage;
